import { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { IdentityUserAri } from '@atlassian/ari/identity';
import { type EntityATI, type ThirdPartyFilters } from '@atlassian/search-client';

import { type TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { ProductKeys } from '../../../common/constants/products';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import {
	SearchExperience,
	type SearchStateFilters,
	type SearchStoreExperience,
} from '../../../common/constants/search';
import { type SelectedFiltersType } from '../../../common/constants/types';
import { getThirdPartyFilters } from '../../../common/utils/filters';
import { isAllProductSearch } from '../../../common/utils/product';
import { getProducts } from '../../../common/utils/search';
import { useBootstrap, useCurrentUserId } from '../../store/bootstrap';
import { getTypeEntities, useSearch, useSelectedDatasource } from '../../store/search';
import {
	archivedAndPendingComponentsFilter,
	archivedFilter,
	assetsSchemaFilter,
	assetsTypeFilter,
	assigneeFilter,
	closeDateFilter,
	commentedByMeFilter,
	contentFilter,
	contributorFilter,
	createdByFilter,
	focusAreaTypeFilter,
	focusAreaUnderFilter,
	labelFilter,
	lastUpdatedByFilter,
	lastUpdatedFilter,
	opportunityStatusFilter,
	ownerFilter,
	pageStatusFilter,
	positionEmploymentTypeFilter,
	positionFocusAreaFilter,
	positionJobFamilyFilter,
	positionStatusFilter,
	positionTeamFilter,
	projectFilter,
	reporterFilter,
	salesforceStageFilter,
	sortFilter,
	spaceFilter,
	statusFilter,
	titleMatchFilter,
	verifiedFilter,
	watchedFilter,
} from '../../store/search/filters';

import {
	getArchivedAndPendingComponentsValue,
	getBooleanFilterValue,
	getContainerStatusValue,
	getContentStatusValue,
	getFilterDateRange,
	getJiraIssueStatusFilterOptions,
	getJiraProjectFilter,
	getSelectedDateRange,
	getSelectedJiraWorkType,
	getSelectFilterValue,
	getSortFilters,
	omitNonJiraFilterValues,
} from './utils';

export type SelectedFiltersReturnType = {
	selectedFilters: SelectedFiltersType;
	unselectedProductsThirdPartyFilters: ThirdPartyFilters;
	hasFiltersSelected: boolean;
	selectedEntities: string[];
	entitiesForResultsCountQuery: EntityATI[];
	entities: EntityATI[];
};

type GetSelectedFiltersPropsFromBootstrap = {
	availableProducts: ProductKeys[];
	allFilterExcludedProducts: ProductKeys[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	currentUserId?: string | null;
};

type GetSelectedFiltersPropsFromSearch = {
	filters: SearchStateFilters;
	selectedProducts: ProductKeys[];
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	jsmIncidentFilterIds: string[];
	experience?: SearchStoreExperience;
};

type GetSelectedFiltersProps = GetSelectedFiltersPropsFromBootstrap &
	GetSelectedFiltersPropsFromSearch;

export const getSelectedFilters = ({
	filters,
	selectedProducts,
	selectedDatasource,
	availableProducts: products,
	allFilterExcludedProducts,
	thirdPartyConfigs,
	currentUserId,
	jsmIncidentFilterIds,
	experience = SearchExperience.SEARCH_PAGE,
}: GetSelectedFiltersProps): SelectedFiltersReturnType => {
	const { selectedProduct, filteredProducts, availableProducts } = getProducts({
		selectedProducts,
		availableProducts: products,
	});
	const { selectedEntities, entitiesForQuery, entitiesForResultsCountQuery, selectedTypes } =
		getTypeEntities(
			{ availableProducts, filters, selectedProducts, allFilterExcludedProducts },
			{
				thirdPartyConfigs,
			},
		);

	const watchedByMeValue = getBooleanFilterValue(filters[watchedFilter.id]);
	const titleMatchValue = getBooleanFilterValue(filters[titleMatchFilter.id]);
	const selectedDateRange = getSelectedDateRange(filters[lastUpdatedFilter.id]);
	const filterDateRange = getFilterDateRange(filters[lastUpdatedFilter.id]);
	const commonContributors = getSelectFilterValue(filters[contributorFilter.id]).length
		? getSelectFilterValue(filters[contributorFilter.id])
		: undefined;
	const pageStatusValue = getSelectFilterValue(filters[pageStatusFilter.id])[0];

	const assigneeARIValues = getSelectFilterValue(filters[assigneeFilter.id]).map((assigneeId) => {
		return `ari:cloud:identity::user/${assigneeId}`;
	});
	const reporterARIValues = getSelectFilterValue(filters[reporterFilter.id]).map((reporterId) => {
		return `ari:cloud:identity::user/${reporterId}`;
	});

	const watcherARIValues = (watchedByMeValue && currentUserId ? [currentUserId] : []).map(
		(watcherId) => {
			return `ari:cloud:identity::user/${watcherId}`;
		},
	);

	const commenterARIValues =
		getBooleanFilterValue(filters[commentedByMeFilter.id]) &&
		currentUserId &&
		fg('rovo_search_in_jira_commenter_filter')
			? [`ari:cloud:identity::user/${currentUserId}`]
			: [];

	const assignee3PValues = getSelectFilterValue(filters[assigneeFilter.id]).map((assigneeId) => {
		if (assigneeId.startsWith('ari:cloud:')) {
			return IdentityUserAri.parse(assigneeId).resourceId;
		}
		return assigneeId;
	});

	let containerAris = [
		...getSelectFilterValue(filters[projectFilter.id]),
		...getSelectFilterValue(filters[spaceFilter.id]),
	];

	const createdByValues = getSelectFilterValue(filters[createdByFilter.id]);
	const labelsValues = getSelectFilterValue(filters[labelFilter.id]);
	const lastUpdatedByValues = getSelectFilterValue(filters[lastUpdatedByFilter.id]);
	const ancestorAris = getSelectFilterValue(filters[contentFilter.id]);
	const ownerValues = getSelectFilterValue(filters[ownerFilter.id]);
	const opportunityStatusValue = getSelectFilterValue(filters[opportunityStatusFilter.id])[0];
	const salesforceStageValues = getSelectFilterValue(filters[salesforceStageFilter.id]);

	const sortFilters = getSortFilters(filters[sortFilter.id]);

	// Get close date custom value for Salesforce filter
	const closeDateFilterState = filters[closeDateFilter.id];
	const closeDateCustomValue =
		closeDateFilterState &&
		'customValue' in closeDateFilterState &&
		closeDateFilterState.customValue
			? (closeDateFilterState.customValue as Record<string, string>).closeDateVal
			: undefined;

	const isAllProductsSelected = isAllProductSearch(selectedProducts);

	const selectedFilters = {
		commonFilters: {
			range: {
				lastModified: selectedDateRange,
			},
			contributorsFilter: commonContributors,
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: omitNonJiraFilterValues(
					getSelectFilterValue(filters[projectFilter.id]),
					selectedProduct,
				),
				watcherARIs: omitNonJiraFilterValues(watcherARIValues, selectedProduct),
				assigneeARIs: omitNonJiraFilterValues(assigneeARIValues, selectedProduct),
				issueTypeIDs: jsmIncidentFilterIds, // fg=false branch not implemented in relay due to fetch request and dependency on intl
				issueLabels: omitNonJiraFilterValues(
					getSelectFilterValue(filters[labelFilter.id]),
					selectedProduct,
				),
				statusCategories: omitNonJiraFilterValues(
					getJiraIssueStatusFilterOptions(filters[statusFilter.id]),
					selectedProduct,
				),
				reporterARIs: omitNonJiraFilterValues(reporterARIValues, selectedProduct),
				commenterARIs: omitNonJiraFilterValues(commenterARIValues, selectedProduct),
				...(fg('rovo_jira_work_type')
					? { issueTypes: getSelectedJiraWorkType(selectedTypes, selectedProduct) }
					: {}),
			},
			projectFilter: {
				projectTypes:
					experience === SearchExperience.QUICK_FIND ? [] : getJiraProjectFilter(selectedProduct),
			},
		},
		assetsFilters: {
			objectSchemaFilter: {
				schemaARIs: fg('assets_rovo_search_all')
					? getSelectFilterValue(filters[assetsSchemaFilter.id])
					: [],
			},
			objectTypeFilter: {
				typeARIs: fg('assets_rovo_search_all')
					? getSelectFilterValue(filters[assetsTypeFilter.id])
					: [],
			},
		},
		confluenceFilters: {
			spacesFilter: getSelectFilterValue(filters[spaceFilter.id]),
			isVerified: getBooleanFilterValue(filters[verifiedFilter.id]) || undefined,
			contributorsFilter: getSelectFilterValue(filters[contributorFilter.id]),
			ancestorIdsFilter: getSelectFilterValue(filters[contentFilter.id]),
			range: selectedProduct !== ProductKeys.Confluence ? filterDateRange : [],
			owners: getSelectFilterValue(filters[ownerFilter.id]),
			labelsFilter:
				selectedProduct === ProductKeys.Confluence
					? getSelectFilterValue(filters[labelFilter.id])
					: [],
			titleMatchOnly: titleMatchValue,
			...(pageStatusValue ? { pageStatus: [pageStatusValue] } : {}),
			contentStatuses:
				selectedProduct === ProductKeys.Confluence
					? getContentStatusValue(filters[archivedFilter.id])
					: [],
			containerStatus:
				selectedProduct === ProductKeys.Confluence
					? getContainerStatusValue(filters[archivedFilter.id])
					: [],
		},
		mercuryFilters: {
			owners: getSelectFilterValue(filters[ownerFilter.id]),
			focusAreaTypeIds: getSelectFilterValue(filters[focusAreaTypeFilter.id]),
			ancestorIds: getSelectFilterValue(filters[focusAreaUnderFilter.id]),
		},
		...(fg('talent_global_search')
			? {
					talentFilters: {
						owners: getSelectFilterValue(filters[ownerFilter.id]),
						employmentTypes: getSelectFilterValue(filters[positionEmploymentTypeFilter.id]).map(
							(value) => decodeURIComponent(value),
						),
						focusAreas: getSelectFilterValue(filters[positionFocusAreaFilter.id]),
						jobFamilies: getSelectFilterValue(filters[positionJobFamilyFilter.id]).map((value) =>
							decodeURIComponent(value),
						),
						statuses: getSelectFilterValue(filters[positionStatusFilter.id]).map((value) =>
							decodeURIComponent(value),
						),
						teams: getSelectFilterValue(filters[positionTeamFilter.id]),
					},
				}
			: {}),
		compassFilters: {
			componentFilter: {
				componentStates: getArchivedAndPendingComponentsValue(
					filters[archivedAndPendingComponentsFilter.id],
				),
			},
		},
		thirdPartyFilters: getThirdPartyFilters(
			filteredProducts,
			titleMatchValue,
			selectedEntities as TypeFilterValueKey[],
			thirdPartyConfigs,
			isAllProductsSelected,
			filterDateRange,
			assignee3PValues,
			containerAris,
			createdByValues,
			lastUpdatedByValues,
			ancestorAris,
			labelsValues,
			undefined,
			selectedDatasource?.datasourceId,
			ownerValues,
			salesforceStageValues,
			closeDateCustomValue,
			opportunityStatusValue,
		),
		sortFilters,
	} satisfies SelectedFiltersType;

	const unselectedProductsThirdPartyFilters = getThirdPartyFilters(
		availableProducts,
		titleMatchValue,
		selectedEntities as TypeFilterValueKey[],
		thirdPartyConfigs,
		isAllProductsSelected,
		filterDateRange,
		undefined, // assignees
		undefined, // containerAris
		undefined, // createdBy
		undefined, // lastUpdatedBy
		undefined, // ancestorAris
		undefined, // labels
		undefined, // additionalTexts
		undefined, // selecteddatasourceid
		undefined, // ownerValues
		undefined, // closeDateCustomValue
	);

	const {
		commonFilters,
		jiraFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		talentFilters,
	} = selectedFilters;

	const isEmpty =
		!commonFilters.range.lastModified &&
		!commonFilters.contributorsFilter &&
		selectedEntities.length === 0 &&
		confluenceFilters.spacesFilter.length === 0 &&
		!confluenceFilters.isVerified &&
		!confluenceFilters.titleMatchOnly &&
		confluenceFilters.contentStatuses.length === 0 &&
		confluenceFilters.containerStatus.length === 0 &&
		confluenceFilters.contributorsFilter.length === 0 &&
		confluenceFilters.ancestorIdsFilter.length === 0 &&
		confluenceFilters.range.length === 0 &&
		confluenceFilters.owners.length === 0 &&
		confluenceFilters.labelsFilter.length === 0 &&
		(!confluenceFilters.pageStatus || confluenceFilters.pageStatus.length === 0) &&
		jiraFilters.issueFilter.assigneeARIs.length === 0 &&
		jiraFilters.issueFilter.projectARIs.length === 0 &&
		jiraFilters.issueFilter.statusCategories.length === 0 &&
		jiraFilters.issueFilter.reporterARIs.length === 0 &&
		jiraFilters.issueFilter.commenterARIs.length === 0 &&
		mercuryFilters.owners.length === 0 &&
		jiraFilters.issueFilter.issueLabels.length === 0 &&
		// When cleaning up rovo_jira_work_type change this to jiraFilters.issueFilter.issueTypes.length === 0
		(!jiraFilters.issueFilter.issueTypes || jiraFilters.issueFilter.issueTypes?.length === 0) &&
		mercuryFilters.owners.length === 0 &&
		mercuryFilters.focusAreaTypeIds.length === 0 &&
		mercuryFilters.ancestorIds.length === 0 &&
		compassFilters.componentFilter.componentStates.length === 0 &&
		(fg('talent_global_search')
			? talentFilters?.owners.length === 0 &&
				talentFilters?.employmentTypes.length === 0 &&
				talentFilters?.focusAreas.length === 0 &&
				talentFilters?.jobFamilies.length === 0 &&
				talentFilters?.statuses.length === 0 &&
				talentFilters?.teams.length === 0
			: true) &&
		(fg('assets_rovo_search_all')
			? assetsFilters.objectSchemaFilter.schemaARIs.length === 0 &&
				assetsFilters.objectTypeFilter.typeARIs.length === 0
			: true) &&
		(!thirdPartyFilters.range || Object.keys(thirdPartyFilters.range).length === 0) &&
		!thirdPartyFilters.titleMatchOnly &&
		thirdPartyFilters.subtypes.length === 0 &&
		(!thirdPartyFilters.assignees || thirdPartyFilters.assignees?.length === 0) &&
		(!thirdPartyFilters.lastUpdatedBy || thirdPartyFilters.lastUpdatedBy?.length === 0) &&
		(!thirdPartyFilters.createdBy || thirdPartyFilters.createdBy?.length === 0) &&
		(!thirdPartyFilters.ancestorAris || thirdPartyFilters.ancestorAris?.length === 0) &&
		(!thirdPartyFilters.salesforceFilters?.stage ||
			thirdPartyFilters.salesforceFilters.stage.length === 0) &&
		(!thirdPartyFilters.salesforceFilters?.owners ||
			thirdPartyFilters.salesforceFilters.owners.length === 0) &&
		!thirdPartyFilters.salesforceFilters?.closeDate;
	return {
		selectedFilters,
		unselectedProductsThirdPartyFilters,
		selectedEntities,
		entitiesForResultsCountQuery,
		entities: entitiesForQuery,
		hasFiltersSelected: !isEmpty,
	};
};

/**
 * QS-6991
 *
 * This hook is a shim allowing easy fg('search-page-relay-useselectedfilters-step-1')
 * This is a migration step in moving useSelectedFilters (a hook) to be getSelectedFilters (not a hook!)
 * This unblocks the relay migration and will allow an imperative search query approach
 *
 * In a later PR, we'll begin to pass in all the props needed at the callsites that have useSelectedFilters() and then even remove this hook!
 */
export const useSelectedFilters = ({
	experience = SearchExperience.SEARCH_PAGE,
}: {
	experience?: SearchStoreExperience;
}): SelectedFiltersReturnType => {
	const [search] = useSearch();
	const [selectedDatasource] = useSelectedDatasource();
	const [{ products, allFilterExcludedProducts, thirdPartyConfigs }] = useBootstrap();
	const currentUserId = useCurrentUserId();

	const { filters, selectedProducts, jsmIncidentFilterIds } = search;

	return useMemo(() => {
		return getSelectedFilters({
			filters,
			selectedProducts,
			selectedDatasource,
			availableProducts: products,
			allFilterExcludedProducts,
			thirdPartyConfigs,
			jsmIncidentFilterIds,
			experience,
			currentUserId,
		});
	}, [
		filters,
		selectedProducts,
		selectedDatasource,
		products,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		jsmIncidentFilterIds,
		experience,
		currentUserId,
	]);
};
